import { useMemo, useState } from 'react';
import { Button, Card, Divider, Modal, Segmented, Space, Tag, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';

const { Text, Paragraph, Title } = Typography;

export interface AiRequirementExample {
  title: string;
  requirements: string;
  targetFields: string[];
  protocol?: string;
  note?: string;
}

const P2_EXAMPLES: AiRequirementExample[] = [
  {
    title: 'RTU v1.0：温度 + IO',
    requirements: '从 data.points.temp 取温度，data.io.ai0 取 IO；输出 temp/ai0',
    targetFields: ['temp', 'ai0'],
    protocol: 'rtu-v1',
    note: '输出路由：temp→points，ai0→io',
  },
  {
    title: 'RTU v2.0：0.1℃ + 湿度',
    requirements: '温度单位 0.1℃，湿度 %；输出 temp/humidity',
    targetFields: ['temp', 'humidity'],
    protocol: 'rtu-v2',
    note: '单位换算用 unit=scale 0.1',
  },
  {
    title: '华氏度 → 摄氏度',
    requirements: 'data.p.tempF 是华氏度，转摄氏度 temp；输出 temp',
    targetFields: ['temp'],
    protocol: 'rtu-v2',
    note: '自动补 unit=fToC',
  },
  {
    title: '阈值分级',
    requirements: '温度 >30 输出 level=high，否则 low；输出 temp/level',
    targetFields: ['temp', 'level'],
    protocol: 'rtu-v2',
    note: 'level 类型为文本',
  },
  {
    title: '多计算字段',
    requirements: 'temp；tempF=cToF(temp)；ratio=temp/10；level>30 high；输出 temp/tempF/ratio/level',
    targetFields: ['temp', 'tempF', 'ratio', 'level'],
    protocol: 'rtu-v2',
    note: '可写 `字段=表达式`，AI 会转成 compute',
  },
  {
    title: 'Modbus 两个寄存器',
    requirements: 'data.list[0].v → temp；data.list[1].v → humidity',
    targetFields: ['temp', 'humidity'],
    protocol: 'modbus',
    note: '明确写出路径最稳',
  },
  {
    title: '只输出 IO',
    requirements: '只输出 IO ai0/di1，输出到 io',
    targetFields: ['ai0', 'di1'],
    protocol: 'json',
  },
  {
    title: '缺失默认值',
    requirements: '温度缺失时默认 0；输出 temp/humidity',
    targetFields: ['temp', 'humidity'],
    protocol: 'rtu-v2',
    note: '样例中不存在时用 fallback',
  },
  {
    title: 'points/io 分流',
    requirements: 'temp 输出到 points，ai0 输出到 io',
    targetFields: ['temp', 'ai0'],
    protocol: 'rtu-v1',
  },
];

const P1_EXAMPLES: AiRequirementExample[] = [
  {
    title: '平铺数组',
    requirements: '每行 label/value；输出 label/value',
    targetFields: ['label', 'value'],
    note: '样例最外层是数组时数据路径留空',
  },
  {
    title: '嵌套数组',
    requirements: 'data.items 行；meta.ts→time，meta.value→value',
    targetFields: ['time', 'value'],
    note: '路径会写到 source.path',
  },
  {
    title: '单位换算',
    requirements: 'temp ×0.1 保留 1 位；hum→humidity',
    targetFields: ['temp', 'humidity'],
    note: 'unit=scale 0.1',
  },
  {
    title: '只保留 value>3',
    requirements: '只保留 value>3 的行；输出 value',
    targetFields: ['value'],
    note: '带“只保留/过滤”会自动补 filter',
  },
  {
    title: '阈值分级',
    requirements: 'value>5 输出 level=high，否则 low；输出 value/level',
    targetFields: ['value', 'level'],
  },
  {
    title: '时间序列',
    requirements: 'list 行；ts→time，v→value',
    targetFields: ['time', 'value'],
    note: '适合折线图 label/value',
  },
  {
    title: '多计算 + 比例',
    requirements: 'value；ratio=value/10；level>5 high；输出 value/ratio/level',
    targetFields: ['value', 'ratio', 'level'],
  },
  {
    title: '最多 N 行',
    requirements: '最多 2 行；输出 value',
    targetFields: ['value'],
    note: '带“最多 N 行”会自动补 limit',
  },
];

export interface AiRequirementHelpModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'rows' | 'object';
  onUse: (example: AiRequirementExample) => void;
}

export default function AiRequirementHelpModal({ open, onClose, mode, onUse }: AiRequirementHelpModalProps) {
  const [tab, setTab] = useState<'object' | 'rows'>(mode);
  const examples = useMemo(() => (tab === 'object' ? P2_EXAMPLES : P1_EXAMPLES), [tab]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      title="AI 写需求 · 示例与技巧"
      data-testid="ai-requirement-help"
    >
      <Typography style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          写清「取哪个字段、怎么换算、怎么过滤、输出哪些字段」即可；AI 会生成可视化规格并自动试跑，采用前请核对。
          示例可直接填入 AI 弹窗。
        </Paragraph>
        <Segmented
          size="small"
          value={tab}
          onChange={(v) => setTab(v as 'object' | 'rows')}
          options={[
            { value: 'object', label: '设备报文（P2）' },
            { value: 'rows', label: '数据集行（P1）' },
          ]}
          data-testid="ai-help-mode"
        />
        <Divider style={{ margin: '10px 0' }} />
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {examples.map((ex) => (
            <Card key={ex.title} size="small" data-testid={`ai-help-example-${ex.title}`}>
              <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                <div style={{ minWidth: 0 }}>
                  <Space wrap size={6}>
                    <Text strong>{ex.title}</Text>
                    {ex.protocol && <Tag>{ex.protocol}</Tag>}
                    <Tag color="blue">目标：{ex.targetFields.join(', ')}</Tag>
                  </Space>
                  <Paragraph style={{ margin: '4px 0 0', fontSize: 12 }}>{ex.requirements}</Paragraph>
                  {ex.note && <Text type="secondary" style={{ fontSize: 12 }}>{ex.note}</Text>}
                </div>
                <Button
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => {
                    onUse(ex);
                    onClose();
                  }}
                  data-testid={`ai-help-use-${ex.title}`}
                >
                  填入
                </Button>
              </Space>
            </Card>
          ))}
        </Space>
        <Divider style={{ margin: '10px 0' }} />
        <Title level={5} style={{ marginTop: 0 }}>写需求技巧</Title>
        <Paragraph style={{ fontSize: 12, marginBottom: 4 }}>
          1. 路径尽量写全：<Text code>data.p.temp</Text>；数组写 <Text code>list[0].v</Text>。
        </Paragraph>
        <Paragraph style={{ fontSize: 12, marginBottom: 4 }}>
          2. 单位/精度明确：<Text code>0.1℃</Text>、<Text code>保留 2 位</Text>、<Text code>华氏转摄氏</Text>。
        </Paragraph>
        <Paragraph style={{ fontSize: 12, marginBottom: 4 }}>
          3. 过滤/分级用“只保留/过滤/否则”等词；输出字段用逗号列出。
        </Paragraph>
        <Paragraph style={{ fontSize: 12, marginBottom: 0 }}>
          4. 复杂计算写 <Text code>字段=表达式</Text>（如 <Text code>tempF=cToF(temp)</Text>）；二进制/跨行窗口请用「代码（高级）」。
        </Paragraph>
      </Typography>
    </Modal>
  );
}
